/**
 * Plans analytics constants. Call `trackEvent` (or helpers in `plansTrack.ts`) at the
 * click / mount site (mode-specific wrappers), not in shared atomic UI.
 * Pair with `EventNames` from misc.types. Stay within TrackEventPayload fields only.
 *
 * Field map:
 * - ScreenName → product surface + plan mode (use PlansScreen.* or *ScreenByPlanMode?.[planMode])
 * - fundName / fundIsin → fund identity
 * - Fund / tile open → buttonLabel = fund leg (PlansFundRole.* / *By*Action)
 * - CTA with a visible label → buttonLabel = that label; scrollSection = fund leg
 */

import { PlansPlanMode } from '../lumpsum/api/types'
import { LumpsumRecommendationActionType } from '../lumpsum/api/common.types'
import { SipPlanMode } from '../sip/api/types'
import { SipRecommendationActionType } from '../sip/api/common.types'

/** `payload.ScreenName` — product surface + plan mode. Pick the constant that matches the wrapper file. */
export const PlansScreen = {
  LumpsumAllocation: 'Plans lumpsum allocation',
  LumpsumRebalance: 'Plans lumpsum rebalance',
  LumpsumIdeal: 'Plans lumpsum ideal',
  SipYourPlan: 'Plans SIP your plan',
  SipIdeal: 'Plans SIP ideal',
  LumpsumAllocationRecommendation: 'Plans lumpsum allocation recommendation',
  LumpsumRebalanceRecommendation: 'Plans lumpsum rebalance recommendation',
  LumpsumIdealRecommendation: 'Plans lumpsum ideal recommendation',
  SipYourPlanRecommendation: 'Plans SIP your plan recommendation',
  SipIdealRecommendation: 'Plans SIP ideal recommendation',
  LumpsumAllocationTimer: 'Plans lumpsum allocation timer',
  LumpsumRebalanceTimer: 'Plans lumpsum rebalance timer',
  LumpsumIdealTimer: 'Plans lumpsum ideal timer',
  SipYourPlanTimer: 'Plans SIP your plan timer',
  SipIdealTimer: 'Plans SIP ideal timer'
} as const

/** List ScreenName by lumpsum tab — use `PlansLumpsumListScreenByPlanMode?.[planMode]`. */
export const PlansLumpsumListScreenByPlanMode = {
  [PlansPlanMode.ALLOCATION]: PlansScreen.LumpsumAllocation,
  [PlansPlanMode.REBALANCE]: PlansScreen.LumpsumRebalance,
  [PlansPlanMode.IDEAL_PORTFOLIO]: PlansScreen.LumpsumIdeal
} as const

/** List ScreenName by SIP tab — use `PlansSipListScreenByPlanMode?.[planMode]`. */
export const PlansSipListScreenByPlanMode = {
  [SipPlanMode.YOUR_PLAN]: PlansScreen.SipYourPlan,
  [SipPlanMode.IDEAL_SIPS]: PlansScreen.SipIdeal
} as const

/** Recommendation sheet ScreenName by lumpsum plan mode. */
export const PlansLumpsumRecommendationScreenByPlanMode = {
  [PlansPlanMode.ALLOCATION]: PlansScreen.LumpsumAllocationRecommendation,
  [PlansPlanMode.REBALANCE]: PlansScreen.LumpsumRebalanceRecommendation,
  [PlansPlanMode.IDEAL_PORTFOLIO]: PlansScreen.LumpsumIdealRecommendation
} as const

/** Buy / sell / SIP action leg — use on fund tile / card hits when applicable. */
export const PlansFundRole = {
  Buy: 'buy',
  Sell: 'sell',
  StartSip: 'start_sip',
  StopSip: 'stop_sip',
  IncreaseSip: 'increase_sip',
  DecreaseSip: 'decrease_sip',
  NoAction: 'no_action',
  Ideal: 'ideal',
  UnderReview: 'under_review',
  Executed: 'executed',
  Invalidated: 'invalidated'
} as const

export const PlansFundRoleByLumpsumAction = {
  [LumpsumRecommendationActionType.BUY]: PlansFundRole.Buy,
  [LumpsumRecommendationActionType.SELL]: PlansFundRole.Sell
} as const

export const PlansFundRoleBySipAction = {
  [SipRecommendationActionType.START]: PlansFundRole.StartSip,
  [SipRecommendationActionType.STOP]: PlansFundRole.StopSip,
  [SipRecommendationActionType.INCREASE]: PlansFundRole.IncreaseSip,
  [SipRecommendationActionType.DECREASE]: PlansFundRole.DecreaseSip
} as const

/** `payload.bottomSheetName` / sheet open ids for BottomSheetLoad. */
export const PlansBottomSheet = {
  LumpsumRecommendation: 'plans_lumpsum_recommendation',
  SipRecommendation: 'plans_sip_recommendation',
  PlanDetails: 'plans_plan_details',
  PlanUpdated: 'plans_plan_updated',
  ResetStrategy: 'plans_reset_strategy',
  AllocationPlanInfo: 'plans_allocation_plan_info',
  RebalancePlanInfo: 'plans_rebalance_plan_info',
  MissedGainsInfo: 'plans_missed_gains_info',
  ImportantNote: 'plans_important_note',
  EditIdealSip: 'plans_edit_ideal_sip',
  HighExposureUpsell: 'plans_high_exposure_upsell',
  RegularFundNotSupported: 'plans_regular_fund_not_supported',
  FundNotAllowed: 'plans_fund_not_allowed',
  ModifySip: 'plans_modify_sip'
} as const

/** `payload.eventId` — which control on the lumpsum list. */
export const PlansLumpsumListEventIds = {
  Back: 'PLANS_LUMPSUM_BACK',
  PlanModeTab: 'PLANS_LUMPSUM_PLAN_MODE_TAB',
  ActionFilter: 'PLANS_LUMPSUM_ACTION_FILTER',
  MovementToggle: 'PLANS_LUMPSUM_MOVEMENT_TOGGLE',
  BucketAccordion: 'PLANS_LUMPSUM_BUCKET_ACCORDION',
  PlanInfo: 'PLANS_LUMPSUM_PLAN_INFO',
  MissedGainsInfo: 'PLANS_LUMPSUM_MISSED_GAINS_INFO',
  OptimisedInfo: 'PLANS_LUMPSUM_OPTIMISED_INFO',
  StrategyLandingCta: 'PLANS_LUMPSUM_STRATEGY_LANDING_CTA',
  ViewStrategy: 'PLANS_LUMPSUM_VIEW_STRATEGY',
  SeeWhatChanged: 'PLANS_LUMPSUM_SEE_WHAT_CHANGED',
  PlanUpdatedGotIt: 'PLANS_LUMPSUM_PLAN_UPDATED_GOT_IT',
  HighExposureUpsell: 'PLANS_LUMPSUM_HIGH_EXPOSURE_UPSELL',
  FooterPlanDetails: 'PLANS_LUMPSUM_FOOTER_PLAN_DETAILS',
  ResetStrategy: 'PLANS_LUMPSUM_RESET_STRATEGY',
  ResetStrategyConfirm: 'PLANS_LUMPSUM_RESET_STRATEGY_CONFIRM',
  ViewRebalanceRiskProfile: 'PLANS_LUMPSUM_VIEW_REBALANCE_RISK_PROFILE',
  FaqAccordion: 'PLANS_LUMPSUM_FAQ_ACCORDION',
  FaqViewAll: 'PLANS_LUMPSUM_FAQ_VIEW_ALL',
  UnlockElite: 'PLANS_LUMPSUM_UNLOCK_ELITE',
  ListRetry: 'PLANS_LUMPSUM_LIST_RETRY'
} as const

/** `payload.eventId` — which control on the SIP list. */
export const PlansSipListEventIds = {
  Back: 'PLANS_SIP_BACK',
  PlanModeTab: 'PLANS_SIP_PLAN_MODE_TAB',
  ActionFilter: 'PLANS_SIP_ACTION_FILTER',
  BucketAccordion: 'PLANS_SIP_BUCKET_ACCORDION',
  EditIdealSip: 'PLANS_SIP_EDIT_IDEAL_SIP',
  EditIdealSipConfirm: 'PLANS_SIP_EDIT_IDEAL_SIP_CONFIRM',
  StrategyLandingCta: 'PLANS_SIP_STRATEGY_LANDING_CTA',
  PlanInfo: 'PLANS_SIP_PLAN_INFO',
  ViewStrategy: 'PLANS_SIP_VIEW_STRATEGY',
  FooterPlanDetails: 'PLANS_SIP_FOOTER_PLAN_DETAILS',
  ResetStrategy: 'PLANS_SIP_RESET_STRATEGY',
  ResetStrategyConfirm: 'PLANS_SIP_RESET_STRATEGY_CONFIRM',
  EditExternalSips: 'PLANS_SIP_EDIT_EXTERNAL_SIPS',
  FaqAccordion: 'PLANS_SIP_FAQ_ACCORDION',
  FaqViewAll: 'PLANS_SIP_FAQ_VIEW_ALL',
  UnlockElite: 'PLANS_SIP_UNLOCK_ELITE',
  ListRetry: 'PLANS_SIP_LIST_RETRY'
} as const

/** Shared fund-card event ids. Mode / leg come from ScreenName + buttonLabel / scrollSection. */
export const PlansListEventIds = {
  FundCard: 'PLANS_FUND_CARD',
  FundCardCta: 'PLANS_FUND_CARD_CTA'
} as const

/** `payload.eventId` — lumpsum recommendation bottom sheet. */
export const PlansLumpsumSheetEventIds = {
  Close: 'PLANS_LUMPSUM_SHEET_CLOSE',
  Retry: 'PLANS_LUMPSUM_SHEET_RETRY',
  BannerDismiss: 'PLANS_LUMPSUM_SHEET_BANNER_DISMISS',
  PairTab: 'PLANS_LUMPSUM_SHEET_PAIR_TAB',
  CarouselFund: 'PLANS_LUMPSUM_SHEET_CAROUSEL_FUND',
  PrimaryCta: 'PLANS_LUMPSUM_SHEET_PRIMARY_CTA',
  FundTileOpen: 'PLANS_LUMPSUM_SHEET_FUND_TILE_OPEN',
  ReasonsAccordion: 'PLANS_LUMPSUM_SHEET_REASONS_ACCORDION',
  StepsAccordion: 'PLANS_LUMPSUM_SHEET_STEPS_ACCORDION',
  TaxAccordion: 'PLANS_LUMPSUM_SHEET_TAX_ACCORDION',
  ReasonInfo: 'PLANS_LUMPSUM_SHEET_REASON_INFO',
  ImportantNoteLearnMore: 'PLANS_LUMPSUM_SHEET_IMPORTANT_NOTE'
} as const

/** `payload.eventId` — SIP recommendation bottom sheet. */
export const PlansSipSheetEventIds = {
  Close: 'PLANS_SIP_SHEET_CLOSE',
  Retry: 'PLANS_SIP_SHEET_RETRY',
  BannerDismiss: 'PLANS_SIP_SHEET_BANNER_DISMISS',
  PrimaryCta: 'PLANS_SIP_SHEET_PRIMARY_CTA',
  FundTileOpen: 'PLANS_SIP_SHEET_FUND_TILE_OPEN'
} as const

/** `payload.eventId` — buy / sell / SIP execution + blocking sheets. */
export const PlansExecutionEventIds = {
  Buy: 'PLANS_EXECUTION_BUY',
  Sell: 'PLANS_EXECUTION_SELL',
  SipStart: 'PLANS_EXECUTION_SIP_START',
  SipStop: 'PLANS_EXECUTION_SIP_STOP',
  SipIncrease: 'PLANS_EXECUTION_SIP_INCREASE',
  SipDecrease: 'PLANS_EXECUTION_SIP_DECREASE',
  RegularSheetClose: 'PLANS_EXECUTION_REGULAR_SHEET_CLOSE',
  NotAllowedClose: 'PLANS_EXECUTION_NOT_ALLOWED_CLOSE',
  NotAllowedSupport: 'PLANS_EXECUTION_NOT_ALLOWED_SUPPORT',
  ModifySheetClose: 'PLANS_EXECUTION_MODIFY_SHEET_CLOSE',
  ModifyFolio: 'PLANS_EXECUTION_MODIFY_FOLIO'
} as const

/** `payload.eventId` — pre–rolled-out timer / strategy shells. */
export const PlansTimerEventIds = {
  UnlockElite: 'PLANS_TIMER_UNLOCK_ELITE',
  StrategyCta: 'PLANS_TIMER_STRATEGY_CTA',
  EducationAccordion: 'PLANS_TIMER_EDUCATION_ACCORDION',
  ExplainerVideo: 'PLANS_TIMER_EXPLAINER_VIDEO'
} as const
